import { useQuery } from '@tanstack/react-query';
import type { Permission } from '@gaintplay/core/permissions';
import { ApiError, api } from './api';

export interface Staff {
  id: string;
  email: string;
  name: string;
  /** What this staff member may see and do (their roles' permissions; owners have all). */
  permissions: Permission[];
}

/**
 * Who is signed in (null when nobody is). The Shell and the sign-in page share it, so signing in
 * can fill it before the dashboard reads it.
 */
export const staffQuery = {
  queryKey: ['staff'],
  // Read once on sign-in and reused for a minute, not fetched again by the page that opens next.
  staleTime: 60_000,
  queryFn: async (): Promise<Staff | null> => {
    try {
      const r = await api<{ staff: Omit<Staff, 'permissions'>; permissions: Permission[] }>(
        '/auth/me',
      );
      return { ...r.staff, permissions: r.permissions };
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null;
      throw e;
    }
  },
} as const;

/**
 * `can('withdrawals.decide')`: whether the signed-in staff member has a permission. Pages hide
 * what a role can't use; the API checks again on every request.
 */
export function useCan(): (p: Permission) => boolean {
  const { data } = useQuery(staffQuery);
  return (p) => !!data?.permissions.includes(p);
}
